import React, { useState } from 'react';
import { Box, Typography, Button, TextFieldProps } from '@mui/material';
import { TPTextField } from '../../components/Inputs';

interface SignupFormProps {
  errorMessage?: string;
  curriculum?: string;
  subject?: string;
  initialData?: Partial<SignupFormData>;
  onSubmit?: (data: SignupFormData) => void;
  resetErrorMessage: () => void;
}

interface SignupFormData {
  name: string;
  email: string;
}

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const SignupForm: React.FC<SignupFormProps> = ({
  errorMessage,
  initialData,
  curriculum,
  onSubmit,
}) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    email: initialData?.email || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const submitData: SignupFormData = {
      name: formData.name,
      email: formData.email,
    };

    if (onSubmit) {
      onSubmit(submitData);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderRadius: 3,
        boxShadow: 6,
        padding: { xs: 4, md: 5 },
        maxWidth: 500,
        width: '100%',
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 'bold',
          color: 'gray.900',
          mb: 2,
          fontSize: { xs: '1.75rem', md: '2rem' },
        }}
      >
        Best 1:1 {curriculum || 'Tuitions'}
      </Typography>
      <Typography variant="body1" sx={{ color: 'gray.600', mb: 3 }}>
        Transform your IGCSE Maths preparation with our expert IGCSE Maths live
        1:1 tutoring.
      </Typography>

      {errorMessage && (
        <Box
          sx={{
            backgroundColor: 'error.light',
            color: 'error.dark',
            padding: 2,
            borderRadius: 2,
            mb: 3,
            border: '1px solid',
            borderColor: 'error.main',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {errorMessage}
          </Typography>
        </Box>
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TPTextField
            {...textFieldProps}
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Name"
            required
            fullWidth
            label="Name"
          />

          <TPTextField
            {...textFieldProps}
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="Email Address"
            required
            fullWidth
            label="Email"
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              background: 'linear-gradient(to right, #4A90E2, #9B59B6)',
              fontWeight: 'bold',
              py: 2,
              borderRadius: 2,
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'scale(1.02)',
                boxShadow: 6,
              },
            }}
          >
            Sign Up
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default SignupForm;
